import React, { useState } from 'react';
import { ExternalLink, AlertCircle } from 'lucide-react';

/**
 * EmployeeChip - kompaktes Mitarbeiter-Chip für die Plantafel.
 *
 * Features:
 * - Projektnummer als Hauptbezeichner (fallback: Projektname)
 * - Voller Projektname im Tooltip
 * - Klick auf Projekt-Label → navigiert zur Projekt-Detail-Seite
 * - Cross-KSt-Kennzeichnung automatisch abgeleitet
 * - Kompaktes Layout (1 Zeile wo möglich)
 */
/**
 * Farbe eines Chips.
 *
 * Grundsätzlich die Farbe der Kostenstelle – so sieht man auf der Plantafel
 * sofort, wessen Leute wo arbeiten. Zwei Arten brechen das absichtlich:
 *
 *   Azubi         gelb    – darf nicht allein auf eine Baustelle
 *   Leiharbeiter  violett – kostet anders, ist nicht unbegrenzt verfügbar
 *                           und gehört nicht zur Stammbesetzung
 *
 * Beide Fälle muss man beim Planen auf einen Blick erkennen, nicht erst beim
 * Lesen des Kürzels am Rand. Zusätzlich bleibt die gestrichelte Umrandung aus
 * dem CSS – Farbe allein genügt nicht, wenn jemand sie schlecht unterscheidet
 * oder der Plan schwarzweiß ausgedruckt wird.
 */
export function chipFarbe(employee, costCenter) {
  if (employee?.type === 'Azubi') return '#f9a825';
  if (employee?.type === 'Leiharbeiter') return '#7c3aed';
  return costCenter?.color || '#64748b';
}

export function EmployeeChip({
  assignment, employee, project, costCenter, projectCostCenter,
  isAbsent, absenceType, isConflict, isSelected,
  onPointerDown, onContextMenu, onProjectClick,
  dndListeners, dndAttributes, dragging, style, compact = false,
}) {
  if (!employee) return null;
  const color = chipFarbe(employee, costCenter);
  const displayName = `${employee.first_name} ${employee.last_name}`;

  const isCrossKst = !!(projectCostCenter && costCenter && projectCostCenter.id !== costCenter.id);
  const isSchabosTarget = isCrossKst && projectCostCenter?.type === 'Extern';

  const projectMainLabel = project?.project_number?.trim() || project?.name || '';
  const projectFullName = project?.name || '';
  const assignedHours = typeof assignment?.hours === 'number' ? assignment.hours : null;
  const hoursLabel = assignedHours !== null
    ? (Number.isInteger(assignedHours) ? `${assignedHours}h` : `${assignedHours.toString().replace('.', ',')}h`)
    : null;
  const isPartialHours = assignedHours !== null && assignedHours < 8;

  const cls = [
    'employee-chip',
    compact ? 'chip-compact' : '',
    `type-${employee.type.toLowerCase()}`,
    isSelected ? 'selected' : '',
    isAbsent ? 'absent' : '',
    isConflict ? 'conflict' : '',
    dragging ? 'dragging' : '',
  ].join(' ');
  const chipStyle = {
    background: `${color}22`,
    color: '#0f172a',
    borderColor: `${color}88`,
    ['--chip-accent']: color,
    ...style,
  };

  const title = [
    displayName,
    project ? `${project.project_number ? '#' + project.project_number + ' ' : ''}${project.name}` : '',
    isAbsent ? `(${absenceType})` : '',
    isCrossKst ? `→ ${projectCostCenter?.short_code} (${projectCostCenter?.type === 'Extern' ? 'Extern' : 'Ausleihe'})` : '',
  ].filter(Boolean).join(' · ');

  const handleProjectClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (project && onProjectClick) onProjectClick(project);
  };

  return (
    <div
      className={cls}
      style={chipStyle}
      onPointerDown={onPointerDown}
      onContextMenu={onContextMenu}
      data-testid={`employee-chip-${assignment?.id}`}
      title={title}
      {...(dndAttributes || {})}
      {...(dndListeners || {})}
    >
      <div className="flex items-center gap-1.5 min-w-0 w-full">
        <span className="truncate font-semibold text-[11px] flex-shrink-0" style={{ maxWidth: 92 }}>{displayName}</span>
        {employee.type === 'Azubi' && <span className="text-[9px] font-bold bg-yellow-600 text-white px-1 rounded flex-shrink-0">AZ</span>}
        {employee.type === 'Leiharbeiter' && <span className="text-[8px] font-bold bg-violet-700 text-white px-1 rounded flex-shrink-0">LH</span>}
        {employee.type === 'Praktikant' && <span className="text-[8px] font-bold bg-slate-600 text-white px-1 rounded flex-shrink-0">PR</span>}
        {isConflict && <AlertCircle size={11} className="text-rose-700 flex-shrink-0" />}
        {hoursLabel && !isAbsent && (
          <span
            className={`text-[9px] font-bold px-1 rounded flex-shrink-0 font-mono ${isPartialHours ? 'bg-amber-200 text-amber-900' : 'bg-slate-200 text-slate-700'}`}
            title={`${hoursLabel} an diesem Tag`}
            data-testid={`chip-hours-${assignment?.id}`}
          >
            {hoursLabel}
          </span>
        )}
        {isAbsent ? (
          <span className="text-[10px] italic font-normal text-slate-600 truncate ml-auto">{absenceType}</span>
        ) : (
          <button
            type="button"
            onClick={handleProjectClick}
            onPointerDown={(e) => e.stopPropagation()}
            className="ml-auto inline-flex items-center gap-0.5 text-[10px] font-mono font-semibold text-slate-800 hover:text-sky-700 hover:underline truncate"
            style={{ maxWidth: 110 }}
            data-testid={`chip-project-link-${assignment?.id}`}
          >
            {isSchabosTarget && <span className="text-[9px] font-bold text-purple-800 whitespace-nowrap">→S:</span>}
            {isCrossKst && !isSchabosTarget && (
              <span className="text-[9px] font-bold whitespace-nowrap" style={{ color: projectCostCenter?.color || '#334155' }}>
                →{projectCostCenter?.short_code}:
              </span>
            )}
            <span className="truncate">{projectMainLabel}</span>
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * EmployeeListItem - für die Employee-Palette (Sidebar).
 * Drag & Drop: zieht Mitarbeiter auf Plantafel-Zelle → löst Auto-Dialog aus.
 */
export function EmployeeListItem({ employee, costCenter, dndAttributes, dndListeners, setNodeRef, dragging, style }) {
  const color = chipFarbe(employee, costCenter);
  const cls = [
    'employee-chip',
    'chip-compact',
    `type-${employee.type.toLowerCase()}`,
    dragging ? 'dragging' : '',
  ].join(' ');
  const chipStyle = {
    background: `${color}22`,
    color: '#0f172a',
    borderColor: `${color}88`,
    ['--chip-accent']: color,
    width: '100%',
    cursor: 'grab',
    ...style,
  };
  return (
    <div
      ref={setNodeRef}
      className={cls}
      style={chipStyle}
      data-testid={`palette-employee-${employee.id}`}
      title={`${employee.first_name} ${employee.last_name} (${employee.type})`}
      {...(dndAttributes || {})}
      {...(dndListeners || {})}
    >
      <div className="flex items-center gap-1.5 w-full min-w-0">
        <span className="truncate font-semibold text-[11px]">{employee.first_name} {employee.last_name}</span>
        {employee.type === 'Azubi' && <span className="text-[9px] font-bold bg-yellow-600 text-white px-1 rounded flex-shrink-0 ml-auto">AZ</span>}
        {employee.type === 'Leiharbeiter' && <span className="text-[8px] font-bold bg-violet-700 text-white px-1 rounded flex-shrink-0 ml-auto">LH</span>}
        {employee.type === 'Praktikant' && <span className="text-[8px] font-bold bg-slate-600 text-white px-1 rounded flex-shrink-0 ml-auto">PR</span>}
      </div>
    </div>
  );
}
